import { z } from "zod";
import {
	OrganizationEntityType,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { CompleteJobDocumentInformation } from "../actions/useExportActions";
import { Logger } from "@/lib/logger/Logger";
import { XmlEndpointsEnum } from "./useExportXml";
import axios from "axios";
import { supabase } from "@/lib/supabase";
import { environment } from "../../lib/utils/environment";
import {
	validateCantonAbbreviation,
	validateGLN,
	validatePhone,
} from "../useForm/form-field-validation-functions";
import { isIBANValid } from "../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-validation-functions";
import { getBankAccount } from "../actions/get-bank-account/get-bank-account";
import { resolveSumexXmlVersion, SumexXmlVersion } from "./sumex-xml-version";
import {
	buildPreflightMessage,
	mapZodIssuesToProblems,
	PreflightProblem,
} from "./sumex-preflight";
import { getCantonFromPostalCode } from "../../lib/utils/utils-functions";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import {
	ProductionCountriesEnum,
	productionCountriesLookup,
} from "../../lib/constants/productionCountries";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { useCentralStore } from "../../store/Central";
import { getItemCode } from "./export-xml-utils";
import { postalCodeToCodeAndCityString } from "../../lib/constants";
import { showNotification } from "../../store/Central/selectors";
import { hasSwissPostalCodes } from "../../lib/utils/country-utils";

function formatDateForSumexApi(date: string): string {
	const parsedDate = new Date(date);
	return isNaN(parsedDate.getTime())
		? ""
		: parsedDate.toISOString().split("T")[0];
}

function formatTimestampzForSumexApi(timestampz: string): string {
	return timestampz?.split(".")[0];
}

export const DocumentFieldsForAPIObject = z.object({
	/**
	 * We do not use ATRTPW (which sets the taxpunktwert) on the API side. This is fine because
	 * every job item has its own TPW, which we can safely assume because the tp_value column is not nullable.
	 * Having tp_values be not nullable is ok, because there are always upstream entities to set the tp_value even if the
	 * job item itself does not have one set (client tp_value, organization tp_value)
	 */
	ip: z.object({
		LABCanton: z.string().refine((val) => validateCantonAbbreviation(val), {
			message: "Die Postleitzahl der Organisation ist ungültig",
		}),
		LABCompany: z
			.string()
			.min(1, { message: "Name der Organisation benötigt" }),
		LABName: z
			.string()
			.min(1, { message: "Name der Organisation benötigt" }),
		LABStreet: z.string().catch(""),
		LABCity: z
			.string()
			.min(1, { message: "Stadt der Organisation benötigt" }),
		LABPhone: z.string().catch(""),
		LABEmail: z.string().catch(""),
		LABVATNumber: z.string().catch(""),
		/**
		 * The 4.50 module accepts an empty GLN (labs without one have always
		 * exported this way), so empty passes; a present GLN must be valid to
		 * catch typos. 5.00 hard-requires it for SetBillerGLN — enforced in the
		 * superRefine below, like LABIBAN.
		 */
		LABGLN: z.string().refine((val) => val === "" || validateGLN(val), {
			message:
				"Die GLN der Organisation ist ungültig (13 Ziffern erforderlich)",
		}),
		LABPLZ: z
			.string()
			.min(1, { message: "Postleitzahl der Organisation benötigt" }),
		/**
		 * Only required for 5.00, where the schema mandates a payment part
		 * (esrQRRed) and the module needs the lab's QR-IBAN for it. 4.50 uses a
		 * fixed fake ESR and ignores this field. Enforced in the superRefine
		 * below, because the requirement depends on xmlVersion.
		 */
		LABIBAN: z.string().catch(""),
		ATRCode: z.string().min(1, { message: "Auftragscode benötigt" }),
		ATRNote: z.string().catch(""),
		ATRVAT: z
			.number()
			.min(0, { message: "MwSt. muss eine positive Zahl sein" }),
		ATRProduction: z.string().catch(""),
		ATRDate: z.string().min(1, { message: "Auftragsdatum benötigt" }),
		ATRDiscount1: z
			.number()
			.min(0, { message: "Rabatt 1 muss eine positive Zahl sein" }),
		ATRDiscount2: z
			.number()
			.min(0, { message: "Rabatt 2 muss eine positive Zahl sein" }),
		PATCode: z.string().min(1, { message: "Patientencode benötigt" }),
		PATSalutation: z.string().catch(""),
		PATFirstName: z
			.string()
			.min(1, { message: "Vorname des Patienten benötigt" }),
		PATLastName: z
			.string()
			.min(1, { message: "Nachname des Patienten benötigt" }),
		PATBirthDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
			message: "Geburtsdatum des Patienten fehlt oder ist ungültig",
		}),
		/**
		 * PATSSN (patient AHV number) is deliberately NOT transmitted, even though
		 * 5.00 marks patient/@ssn as required. The lab's XML only carries service
		 * positions; the dentist's own submission supplies the real SSN. Exporting a
		 * sensitive identifier for no functional gain is what we avoid here.
		 * Re-enable only if a PMS demands it. See docs/specs/sumex-xml-500.md §2.2.
		 *
		 * PATSSN: z.string().catch(""),
		 */
		PATProvider: z
			.string()
			.nullable()
			.transform((val) => val ?? ""),
		KUNCode: z.string().min(1, { message: "Auftraggebercode benötigt" }),
		KUNSalutation: z.string().catch(""),
		KUNFirstName: z.string().catch(""),
		KUNLastName: z.string().min(1, {
			message: "Name oder Firmenname des Auftraggebers wird benötigt",
		}),
		KUNStreet: z
			.string()
			.min(1, { message: "Strasse des Kunden wird benötigt" }),
		KUNCity: z
			.string()
			.min(1, { message: "Stadt des Kunden wird benötigt" }),
		KUNPhone: z
			.string()
			.nullable()
			.refine((val) => val === null || validatePhone(val), {
				message: "Die Telefonnummer ist ungültig",
			})
			.catch(""),
		KUNEmail: z.string().catch(""),
		KUNPLZ: z
			.string()
			.min(1, { message: "Postleitzahl des Kunden benötigt" }),
	}),
	documentTypes: z.array(
		z.object({
			code: z.string().min(1, {
				message:
					"Der Code der Position fehlt oder ist ungültig (Falls es sich um einen Artikel handelt, muss der Code 2100.0, 2100.0, 2130.0, 2150.0, 2200.0, 2300.0, 2400.0, 2900.0, 3100.0, 3110.0, 3170.0, 3500.0 oder 3700.0 lauten).",
			}),
			description: z.string().min(1, {
				message: "Bei einer der Positionen fehlt die Beschreibung",
			}),
			quantity: z
				.number()
				.min(0, { message: "Menge muss eine positive Zahl sein" }),
			discount: z.number().catch(0),
			Date: z.string().min(1, {
				message: "Bei einer der Positionen fehlt das Datum",
			}),
			price: z
				.number()
				.min(0, { message: "Preis muss eine positive Zahl sein" }),
			TPW: z.number().min(0, {
				message: "Taxpunktwert muss eine positive Zahl sein",
			}),
			Art: z
				.number()
				.min(0, { message: "Typ muss eine positive Zahl sein" }),
		})
	),
	/**
	 * Resolved per client. Anything the microservice does not recognise falls back
	 * to 450, so an un-migrated service instance still produces a correct document.
	 * See docs/specs/sumex-xml-500.md §3.2.
	 */
	xmlVersion: z.nativeEnum(SumexXmlVersion).default(SumexXmlVersion.V450),
});

export const DocumentFieldsForAPI = DocumentFieldsForAPIObject.superRefine(
	(data, ctx) => {
		if (
			data.xmlVersion === SumexXmlVersion.V500 &&
			!isIBANValid(data.ip.LABIBAN)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["ip", "LABIBAN"],
				message:
					"Die QR-IBAN des Bankkontos fehlt oder ist ungültig (für SUMEX 5.00 benötigt)",
			});
		}
		if (
			data.xmlVersion === SumexXmlVersion.V500 &&
			!validateGLN(data.ip.LABGLN)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["ip", "LABGLN"],
				message:
					"Die GLN der Organisation fehlt oder ist ungültig (für SUMEX 5.00 benötigt)",
			});
		}
	}
);

interface FormatDocumentPropsForApiProps {
	documentProps: CompleteJobDocumentInformation & {
		patient: PatientEntityType;
		organization: OrganizationEntityType;
	};
	documentType:
		| JobDocumentTypeEnum.Quotation
		| JobDocumentTypeEnum.DeliveryNote;
}

export const formatDocumentPropsForApi = (
	props: FormatDocumentPropsForApiProps
): {
	success: boolean;
	data: null | z.infer<typeof DocumentFieldsForAPI>;
	error: string | null;
	problems: PreflightProblem[];
} => {
	const {
		documentProps: {
			organization,
			patient,
			client,
			job,
			jobDocument,
			jobItems,
		},
		documentType,
	} = props;
	const { profile, bankAccountsLookup } = useCentralStore.getState();

	if (
		jobItems.find((item) => item.type == JobItemTypeEnum.ARTICLE_FIXED_RATE)
	) {
		return {
			success: false,
			data: null,
			error: "Artikel vom Typ Arbeitspauschale wurden übersprungen. Versicherer akzeptieren keine undefinierten Aufwändungen.",
			problems: [],
		};
	}

	if (
		!client.city &&
		hasSwissPostalCodes(client.country) &&
		client.postal_code
	) {
		client.city =
			postalCodeToCodeAndCityString[client.postal_code].split(" ")[1];
		showNotification({
			message:
				"Stadt des Kunden wurde automatisch auf " +
				client.city +
				" gesetzt.",
			type: "info",
		});
	}

	const xmlVersion = resolveSumexXmlVersion(client.sumex_xml_version);

	// Same resolution order as the invoice PDFs (job -> client -> lab default).
	// Only 5.00 needs the QR-IBAN; a missing account is caught by the schema's
	// superRefine so 4.50 exports are unaffected.
	const { bankAccount } = getBankAccount({
		bankAccountsLookup,
		lab: organization,
		job,
		recipient: client,
	});

	const data = {
		ip: {
			LABCanton: organization.postal_code
				? getCantonFromPostalCode(Number(organization.postal_code))
				: "",
			LABCompany: organization.name,
			LABName: organization.name,
			LABStreet: organization.street,
			LABCity: organization.city,
			LABPhone: organization.phone_and_fax,
			LABEmail: organization.email ?? profile?.email,

			// LABVATNumber is only used for invoice requests (deliery notes)
			LABVATNumber:
				job.mwst_number ??
				client.mwst_number ??
				organization.mwst_number,
			// Trimmed: a stray space would fail the 13-digit check even though the
			// number itself is fine.
			LABGLN: organization.gln?.trim() ?? "",
			LABPLZ: organization.postal_code,
			LABIBAN: bankAccount?.qr_iban?.replaceAll(" ", "") ?? "",

			/**
			 * ATRCode
			 *
			 * For generalInvoiceRequest450.SetInvoice
			 * bstrRequestInvoiceID	The request ID is the main software's identification of the invoice ("Rechnungsnummer").
			 *
			 * For generalCreditRequest450.SetCreditObject
			 * The request credit ID is the main software's identification of the general credit ("Kostengutsprachenummer").
			 */
			ATRCode: job.code,

			/**
			 * ATRNote
			 *
			 * Only relevant for generalInvoiceRequest450.AddDiagnosis:
			 * bstrText	The textual description of the diagnosis as given in the corresponding diagnosis catalog defined by DiagnosisType .
			 */
			ATRNote:
				documentType === JobDocumentTypeEnum.DeliveryNote
					? job.title
					: "",
			ATRVAT: Number(job.tax_rate),
			ATRProduction:
				job.prod_country_choice === "other"
					? job.prod_country_other
					: productionCountriesLookup[
							job.prod_country_choice as ProductionCountriesEnum
						],

			/**
			 * ATRDate
			 *
			 * For generalInvoiceRequest450.SetInvoice
			 * The request date is the main software's date of the invoice request ("Rechnungsdatum")
			 *
			 * For generalCreditRequest450.SetCreditObject
			 * The request credit date is the main software's date of the general credit ("Kostengutsprachedatum")
			 */
			ATRDate: formatDateForSumexApi(jobDocument.date),
			ATRDiscount1:
				jobDocument.discount_work !== undefined
					? Number(jobDocument.discount_work)
					: undefined,
			ATRDiscount2:
				jobDocument.discount_material !== undefined
					? Number(jobDocument.discount_material)
					: undefined,

			/**
			 * PATCode - Currently, this is not used in the API of either manager!
			 */
			PATCode: patient.code ?? "XX-0000",
			PATSalutation: patient.title,
			PATFirstName: patient.first_name,
			PATLastName: patient.last_name,
			PATBirthDate: formatDateForSumexApi(patient.birth_date ?? ""),
			/**
			 * PATProvider - Currently, this is not used in the API of either manager!
			 */
			PATProvider: "SUVA",

			/**
			 * KUNCode - Currently, this is not used in the API of either manager!
			 */
			KUNCode: client.code ?? "XX-0000",

			KUNSalutation: client.is_company ? "" : client.title || "",
			KUNFirstName: client.is_company ? "" : client.first_name || "",
			KUNLastName: client.is_company
				? client.company_name || ""
				: client.last_name || "",
			KUNStreet: client.street,
			KUNCity: client.city,
			KUNPhone:
				client.phone_business ??
				client.phone_mobile ??
				client.phone_personal ??
				"",
			KUNEmail: client.email || "",
			KUNPLZ: client.postal_code,
		},
		documentTypes: jobItems.map((item) => ({
			/**
			 * When the job item is of type tariff, the standard tariff code can be used.
			 * For articles, the code is used only internally and for the insurance the code is derived from the cluster.
			 */
			code: getItemCode(item),
			description: item.description,
			quantity:
				item.quantity !== undefined ? Number(item.quantity) : undefined,
			discount:
				item.discount !== undefined ? Number(item.discount) : undefined,
			Date: formatTimestampzForSumexApi(
				item.modified_at ?? new Date().toISOString()
			),
			price: item.price !== undefined ? Number(item.price) : undefined,
			TPW: item.tp_value !== undefined ? Number(item.tp_value) : 1,
			Art: item.type !== undefined ? Number(item.type) : null,
		})),
		xmlVersion,
	};

	const validatedData = DocumentFieldsForAPI.safeParse(data);

	if (!validatedData.success) {
		Logger.error(validatedData.error, {}, "Error validating data for XML");
		const problems = mapZodIssuesToProblems(validatedData.error.issues);
		return {
			success: false,
			data: null,
			error: buildPreflightMessage(problems, xmlVersion),
			problems,
		};
	}

	return {
		success: true,
		data: validatedData.data,
		error: null,
		problems: [],
	};
};

// The XML service is only reachable through the express-backend, which holds the
// API key and pins the service certificate. Authentication is the user's Supabase
// session (spec: sumex-service-infrastructure.md).
export const postDataToApi = async ({
	endpoint,
	data,
}: {
	endpoint: XmlEndpointsEnum;
	data: z.infer<typeof DocumentFieldsForAPI>;
}) => {
	const {
		data: { session },
	} = await supabase.auth.getSession();

	return axios({
		method: "POST",
		url: `${environment.VITE_BACKEND_URL}/sumex/${endpoint}`,
		data,
		headers: { Authorization: `Bearer ${session?.access_token}` },
		timeout: 90_000,
	});
};
